import { Injectable, effect } from '@angular/core';
import { Platform } from '@ionic/angular';
import { defer, Observable, of, Subscription } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';
import { UserService } from '../user/user.service';
import { Broadcaster } from './broadcaster.class';
import * as _ from 'lodash';
import { first } from 'rxjs/operators';
import Pusher from 'pusher-js';
import { Logger, LoggingService } from 'ionic-logging-service';
import { EnvironmentService } from '../environment/environment.service';
import { AuthService } from '../auth/auth.service';
import { DialogService } from '../dialog/dialog.service';

// Detect a dead Pusher socket (e.g. after mobile backgrounding) in ~30s instead
// of pusher-js's ~120s default, so its own reconnect kicks in promptly.
export const PUSHER_ACTIVITY_TIMEOUT_MS = 30000;

export const CHANNELS = {
  ALL_TENANT_USERS: {
    CHANNEL_NAME: "TENANT_USER",
    EVENTS: {
      PRINTER_ADDED: "printer_added",
      PRINTER_REMOVED: "printer_removed",
      PRINTER_UPDATE: "printer_updated",
      PRINTER_STATUS_UPDATE: "printer_status_update",
      PRINTER_CREATION_UPDATE: "printer_creation_update",
      WORKSTATION_NETWORK_CHANGED: "workstation_network_changed",
      PRINT_QUEUE_ADDED: "print_queue_added",
      PRINT_QUEUE_REMOVED: "print_queue_removed",
      PRINT_QUEUE_UPDATED: "print_queue_updated",
      PRINT_QUEUE_GROUPS_UPDATED: "print_queue_groups_updated",
      TENANT_UPDATED: "tenant_updated",
      BILLING_DATA_UPDATED: "billing_data_updated",
      AUTH_CONFIG_UPDATED: "auth_config_updated",
      BATCH_EXECUTED: 'batch_executed',
      SECURE_PRINT_CONFIGURATION_ADDED: 'secure_print_configuration_added',
      SECURE_PRINT_CONFIGURATION_UPDATED: 'secure_print_configuration_updated',
      SECURE_PRINT_CONFIGURATION_REMOVED: 'secure_print_configuration_removed',
      WORKFLOW_CREATED: 'workflow_created',
      WORKFLOW_DELETED: 'workflow_deleted',
      WORKFLOW_UPDATED: 'workflow_updated'
    }
  },
  SPECIFIC_USER: {
    CHANNEL_NAME: "USER",
    EVENTS: {
      JOB_ADDED: "job_added",
      JOB_REMOVED: "job_removed",
      JOB_STATE_CHANGED: "job_state_changed",
      JOB_UPDATED: "job_updated",
      TASK_PROGRESS_USER: "task_progress_user",
      DISCOVERY_REPORT: "discovery_report",
      USER_RELEASE_CREATED: "release_created",
      USER_RELEASE_UPDATED: "release_updated",
      USER_RELEASE_DELETED: "release_deleted",
      USER_SCAN_CREATED: "scan_created",
      USER_SCAN_UPDATED: "scan_updated",
      USER_SCAN_DELETED: "scan_deleted",
      USER_UPDATED: "user_updated"
    }
  }
};

@Injectable({
  providedIn: 'root'
})
export class EventService {

  private logger: Logger;
  private channels: any = _.cloneDeep(CHANNELS);
  private pusher: any;
  private pusherConfig: any = null;
  private removeSecurePrintSubscription: Subscription = null;
  public tenant = this.tenantService.tenantSignal;
  private updateSecurePrintSubscription: Subscription = null;
  public  user = this.userService.userSignal;
  private resumeEnabled = true;
  private isEstablishing = false;
  private listenersStarted = false;
  // Identity (self link) of the tenant the live Pusher client was built for, so
  // the tenant effect can detect an in-app tenant switch (A->B) and rebuild the
  // client onto B's channels. Set on a successful establish, cleared on teardown.
  private connectedTenantId: string | null = null;

  public EULA_ACCEPTED_STORAGE_STRING = 'eula-accepted';
  public EULA_ACCEPTED_STORAGE_KEY = 'is-eula-accepted';

  constructor(
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private dialogService: DialogService,
    private environmentService: EnvironmentService,
    private loggingService: LoggingService,
    private platform: Platform,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.logger = this.loggingService.getLogger("[EventService]");
    const methodName = "ctor";
    this.logger.entry(methodName);

    // Tenant drives the single Pusher client: create it when a tenant appears,
    // rebuild it when the tenant changes (in-app switch A->B), and tear it down
    // when the tenant clears (sign-out). Reading the signal inside the extracted
    // method keeps it tracked as the effect's dependency.
    effect(() => {
      this.reconcilePusherWithTenant();
    });

    // ngOnInit is never invoked on a root service, so wire the app-lifecycle and
    // broadcaster listeners here. Previously these lived in a dead ngOnInit(),
    // which meant the platform.resume -> PLATFORM_RESUMED broadcast (and the
    // DISABLE/ENABLE_RESUME handlers) never ran.
    this.startListeners();
  }

  // Public entry point to (re)establish the connection on demand. The tenant
  // effect normally drives this, so today nothing else calls it.
  public async startEventService() {
    this.logger.info('startEventService()');
    await this.establishPusherConnection();
  }

  private startListeners(): void {
    // Idempotency guard: these platform.resume / broadcaster subscriptions are
    // not individually torn down, so a second invocation would stack duplicate
    // handlers (N PLATFORM_RESUMED broadcasts per foreground). Safe today (called
    // once from the ctor) but guards against any future re-init/tenant-switch call.
    if (this.listenersStarted) {
      return;
    }
    this.listenersStarted = true;

    if (!this.updateSecurePrintSubscription) {
      this.updateSecurePrintSubscription = this.broadcaster.on<string>(CHANNELS.ALL_TENANT_USERS.EVENTS.SECURE_PRINT_CONFIGURATION_UPDATED) // MOVE TO MENU COMONENT
      .subscribe(data => {
        console.log('**PUSHER secure print configuration updated: eventService', data);
        this.logger.info('**PUSHER secure print configuration updated');
      });
    }

    if (!this.removeSecurePrintSubscription) {
      this.removeSecurePrintSubscription = this.broadcaster.on<string>(CHANNELS.ALL_TENANT_USERS.EVENTS.SECURE_PRINT_CONFIGURATION_REMOVED) // MOVE TO MENU COMONENT
      .subscribe(data => {
        console.log('**PUSHER secure print configuration removed: eventService', data);
        this.logger.info('**PUSHER secure print configuration removed');
      });
    }

    this.broadcaster.on('DISABLE_RESUME').subscribe(() => {
      this.logger.info('DISABLE_RESUME');
      this.resumeEnabled = false;
    });

    this.broadcaster.on('ENABLE_RESUME').subscribe(() => {
      this.logger.info('ENABLE_RESUME');
      this.resumeEnabled = true;
    });

    this.platform.resume.subscribe(() => {
      // Self-heal: a DISABLE_RESUME suppresses only the NEXT resume, then the flag
      // resets. Several scan/auth flows broadcast DISABLE_RESUME without a
      // guaranteed ENABLE_RESUME on their error paths (e.g. QR/NFC scan failures);
      // without this reset a single failed scan would wedge resumeEnabled=false for
      // the rest of the session, killing the resume-time Pusher reconnect.
      const resumeWasEnabled = this.resumeEnabled;
      this.resumeEnabled = true;
      if (resumeWasEnabled) {
        this.logger.info('platform resume');
        // Nudge the EXISTING client to reconnect only if the socket actually
        // dropped while backgrounded. Reusing the client (vs. building a new one)
        // avoids orphaning the old socket and an extra connection. If no client
        // exists yet, the tenant effect creates it.
        if (this.hasAcceptedEula() && this.pusher && !this.pusherIsConnected()) {
          this.logger.info('platform resume - reconnecting existing pusher client');
          this.pusher.connect();
        }
        this.broadcaster.broadcast('PLATFORM_RESUMED');
      } else {
        this.logger.info('platform resume - suppressed once (resume was disabled), re-enabling');
      }
    });
  }

  private hasAcceptedEula(): boolean {
    // Check localStorage synchronously
    try {
      const eulaValue = localStorage.getItem(this.EULA_ACCEPTED_STORAGE_KEY);
      return eulaValue === this.EULA_ACCEPTED_STORAGE_STRING;
    } catch (error) {
      this.logger.error('Error checking EULA acceptance:', error);
      return false;
    }
  }

  public startTokenExpireCountDown() {
    this.logger.info('startTokenExpireCountDown() - method deprecated, using pusher connection events instead');
    // Token expiry is now handled by pusher connection state changes
  }


  // Reconcile the single Pusher client with the current tenant. Driven by the
  // tenant effect; extracted so the lifecycle decision is unit-testable without
  // flushing effects.
  private reconcilePusherWithTenant(): void {
    const currentTenant = this.tenant();
    if (currentTenant) {
      // An in-app tenant switch (selectTenant -> activateTenant$ -> getTenantResource)
      // sets tenantSignal straight to B with no intervening null, so the client can be
      // "connected" yet still subscribed to tenant A's channels. Detect that by comparing
      // the live client's tenant identity and rebuild. establishPusherConnection() tears
      // the old client down first, so this moves the subscription from A to B.
      const tenantId = currentTenant.links?.self ?? null;
      const tenantChanged = !!this.pusher && tenantId !== this.connectedTenantId;
      // Build the first client when none exists, or rebuild on a genuine tenant switch.
      // Deliberately gate the no-change case on !this.pusher (not !pusherIsConnected()):
      // an existing-but-unavailable client is mid-reconnect under pusher-js's own backoff
      // (and platform.resume nudges connect()), so the effect re-firing on a navigation's
      // refreshStoredTenant() must not tear it down and rebuild it — that's the churn this
      // PR removed handlePusherReconnection() to avoid.
      if (!this.pusher || tenantChanged) {
        this.logger.info('No live client / tenant changed, (re)establishing Pusher connection');
        this.establishPusherConnection();
      }
    } else if (this.pusher) {
      this.logger.info('Tenant became null, disconnecting Pusher');
      this.teardownPusher();
    }
  }

  private async establishPusherConnection() {
    this.logger.info('establishPusherConnection()');

    // Re-entrancy guard: the tenant effect re-fires on every tenantSignal.set()
    // (most page navigations refresh the tenant). Without this, a tenant change
    // during the async configure could start a second establish in parallel and
    // open a second socket. Allow only one establish at a time.
    if (this.isEstablishing) {
      this.logger.info('establishPusherConnection() - already establishing, skipping');
      return;
    }

    // Check tenant state before attempting connection
    const currentTenant = this.tenant();
    if (!currentTenant) {
      this.logger.error('establishPusherConnection() - Cannot establish connection: tenant is null');
      return;
    }

    this.logger.info('establishPusherConnection() - Tenant exists:', {
      name: currentTenant.tenantData?.name,
      hostingDomain: currentTenant.tenantData?.hostingDomain,
      hasPusherInfo: !!currentTenant.embedded?.PUSHER_CLIENT_INFO
    });

    this.isEstablishing = true;

    // Tear down any existing connection first so we never run two clients at once.
    this.teardownPusher();

    try {
      await this.configurePusherObject();
      this.setupPusherConnectionListeners();
      this.setChannels();
      // Record which tenant this client serves so the effect can detect a later switch.
      this.connectedTenantId = currentTenant.links?.self ?? null;
    } catch (error) {
      this.logger.error('Failed to establish pusher connection:', error);
    } finally {
      this.isEstablishing = false;
    }

    // Recover from a tenant switch (A->B) that landed inside the await above: while
    // this establish was running, the effect's B-reconcile hit the isEstablishing
    // guard and returned early with nothing pending, so we'd otherwise be left on
    // A's channels with no re-fire. Now that isEstablishing is clear, re-reconcile
    // if the live tenant has moved on from the one we just recorded. Guard on
    // this.pusher so a failed establish (no client) doesn't spin re-establishing
    // against a persistent error.
    const liveTenantId = this.tenant()?.links?.self ?? null;
    if (this.pusher && liveTenantId !== this.connectedTenantId) {
      this.logger.info('establishPusherConnection() - tenant changed during establish, reconciling');
      this.reconcilePusherWithTenant();
    }
  }

  private async configurePusherObject() {
    this.logger.info('configurePusherObject()');

    const currentTenant = this.tenant();
    if (!currentTenant?.embedded?.PUSHER_CLIENT_INFO) {
      this.logger.error('configurePusherObject() - Missing tenant or Pusher configuration');
      throw new Error('Missing tenant or Pusher configuration');
    }

    const pusherInfo = currentTenant.embedded.PUSHER_CLIENT_INFO;
    if (!pusherInfo.key || !pusherInfo.cluster || !pusherInfo.channels) {
      this.logger.error('configurePusherObject() - Incomplete Pusher configuration');
      throw new Error('Incomplete Pusher configuration');
    }

    try {
      this.logger.info('pusher_client_info configured with cluster: ' + pusherInfo.cluster);
      const ENDPOINT_API = this.environmentService.getEnvironmentEndpoint['ENDPOINT_API'];
      const authEndpoint = ENDPOINT_API + '/pusher/auth';

      this.pusherConfig = {
        cluster: pusherInfo.cluster,
        forceTLS: true,
        activityTimeout: PUSHER_ACTIVITY_TIMEOUT_MS,
        // pusher-js 7.x channel auth. customHandler runs on every (re)subscribe,
        // including the library's automatic reconnect re-subscribes, so each one
        // pulls a fresh token — letting the connection survive token expiry
        // without a rebuild (the old static auth.headers froze the token once).
        // When customHandler is set, pusher-js ignores transport/endpoint, so the
        // auth flow goes entirely through authorizePusherChannel.
        channelAuthorization: {
          customHandler: (params, callback) => {
            this.authorizePusherChannel(authEndpoint, params, callback);
          }
        }
      };
      this.pusher = new Pusher(pusherInfo.key, this.pusherConfig);
      this.pusher.logToConsole = true;
    } catch (error) {
      this.logger.error('Failed to configure pusher object:', error);
      throw error;
    }
  }

  // Channel authorizer for pusher-js, invoked on every (re)subscribe. Fetches a
  // valid (refreshed when needed) token via getValidTokenForRequest() and posts
  // it to /pusher/auth, so re-auth after a reconnect can't fail on a stale token.
  private authorizePusherChannel(
    authEndpoint: string,
    params: { socketId: string; channelName: string },
    callback: (error: Error | null, authData: any) => void
  ): void {
    const methodName = 'authorizePusherChannel';
    this.authService.getValidTokenForRequest().pipe(first()).subscribe({
      next: (token: string) => {
        // Deliberately uses fetch, not HttpClient: this is pusher-js's one-shot auth
        // callback and we've already attached a freshly-validated token above. Routing
        // through AuthInterceptor would re-refresh + retry on a 401, double-refreshing
        // the token and breaking the single-response contract the customHandler expects.
        fetch(authEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: `socket_id=${encodeURIComponent(params.socketId)}`
            + `&channel_name=${encodeURIComponent(params.channelName)}`
        })
          .then(response => {
            if (!response.ok) {
              throw new Error('pusher auth HTTP ' + response.status);
            }
            return response.json();
          })
          .then(authData => callback(null, authData))
          .catch(error => {
            this.logger.error(methodName + ' - pusher auth request failed:', error);
            callback(error, null);
          });
      },
      error: (error: any) => {
        this.logger.error(methodName + ' - failed to obtain access token:', error);
        this.dialogService.hideLoadingSpinnerDialog();
        callback(error, null);
      }
    });
  }

  private setupPusherConnectionListeners() {
    this.logger.info('setupPusherConnectionListeners()');

    if (!this.pusher || !this.pusher.connection) {
      this.logger.warn('Cannot setup connection listeners - pusher not initialized');
      return;
    }

    this.pusher.connection.bind('connected', () => {
      this.logger.info('Pusher connected');
    });

    this.pusher.connection.bind('disconnected', () => {
      this.logger.info('Pusher disconnected');
    });

    // pusher-js owns reconnection: on 'unavailable'/'failed' its ConnectionManager
    // already retries with exponential backoff, so these handlers only log.
    this.pusher.connection.bind('failed', () => {
      this.logger.error('Pusher connection failed (WebSocket unsupported) - no retry possible');
    });

    this.pusher.connection.bind('unavailable', () => {
      this.logger.warn('Pusher unavailable - pusher-js is retrying automatically');
    });

    // Channel re-subscribes re-auth with a fresh token (authorizePusherChannel),
    // so auth errors self-heal on the next reconnect; just surface them here.
    this.pusher.connection.bind('error', (err) => {
      this.logger.error('Pusher error:', err);
    });

    this.pusher.connection.bind('state_change', (states) => {
      this.logger.info(`Pusher state changed: ${states.previous} -> ${states.current}`);
    });
  }

  private setChannels() {
    this.logger.info('setChannels()');
    const currentTenant = this.tenant();
    const channels = currentTenant?.embedded?.PUSHER_CLIENT_INFO?.channels;
    
    if (!channels) {
      this.logger.error('setChannels() - Missing tenant or Pusher channels configuration');
      return;
    }

    this.logger.info('setChannels() - Subscribing to channels:', Object.keys(channels));
    
    _.forEach(this.channels, (channel) => {
      const channelName = channels[channel.CHANNEL_NAME];
      if (!channelName) {
        this.logger.warn(`setChannels() - Channel ${channel.CHANNEL_NAME} not found in tenant configuration`);
        return;
      }
      
      this.logger.info(`setChannels() - Subscribing to channel: ${channelName}`);
      const channelInstance = this.pusher.subscribe(channelName);

        channelInstance.bind('pusher:subscription_error', (status) => {
          this.logger.error('PUSHER subscription error: ' + JSON.stringify(status));
          
          // If subscription fails due to auth, check tenant state first.
          if (status.error && (status.error.includes('401') || status.error.includes('403'))) {
            const currentTenantCheck = this.tenant();
            if (!currentTenantCheck) {
              this.logger.error('Subscription error: tenant is now null, tearing down pusher');
              this.teardownPusher();
            } else {
              // Tenant still valid. pusher-js does NOT auto-retry a failed private-channel
              // subscription while the socket stays connected — the channel only re-subscribes
              // (with a fresh token via authorizePusherChannel) when the connection drops and
              // reconnects. A persistent 401 here means the token is genuinely unusable (refresh
              // also failed), which the auth layer treats as logged-out; we deliberately don't
              // rebuild the client, to avoid the orphaned-socket churn.
              this.logger.warn('Subscription auth error but tenant exists - channel stays unsubscribed until the next reconnect (no client rebuild)');
            }
          }
        });

        channelInstance.bind('pusher:subscription_succeeded', () => {
          this.logger.info(`Successfully subscribed to channel: ${channel.CHANNEL_NAME}`);
        });

      _.forEach(channel.EVENTS, (event) => {
        this.pusher.bind(event, (data) => {
          this.logPusherData(event, data);
          this.broadcaster.broadcast(event, data);
        });
      });
    });
  }

  private logPusherData(event, data): void {
    this.logger.info('pusher.bind() EVENT: ' + event);
    if (event === 'printer_status_update') {
      this.logger.info('pusher.bind() printer: ' + data['printer']);
      this.logger.info('pusher.bind() pollStatus: ' + data['pollStatus']);
    }
    if (event === 'job_added') {
      this.logger.info('pusher.bind() release: ' + data['job']);
    }
    if (event === 'release_created') {
      this.logger.info('pusher.bind() release created: ' + data['release']);
    }
  }

  public resetPusher(): Observable<void> {
    this.logger.info('resetPusher()');
    this.logger.info('resetPusher() pusher: ' + !!this.pusher);
    this.logger.info('resetPusher() pusherConfig: ' + !!this.pusherConfig);
    return this.disconnectPusher();
  }

  // Observable wrapper for callers that compose teardown in an RxJS chain
  // (e.g. LogoutService via resetPusher()); delegates to teardownPusher().
  private disconnectPusher(): Observable<void> {
    return defer(() => {
      this.teardownPusher();
      return of(void 0);
    });
  }

  // Synchronous, unconditional teardown: unbind, unsubscribe, disconnect and drop
  // the reference regardless of connection state, so exactly one client can ever
  // exist and no orphaned socket keeps auto-reconnecting in the background.
  private teardownPusher(): void {
    this.logger.info('teardownPusher() - has pusher: ' + !!this.pusher);
    if (!this.pusher) {
      return;
    }

    if (this.pusher.connection) {
      this.pusher.connection.unbind_all();
    }
    this.pusher.unsubscribe();
    this.logger.info('DISCONNECTING_PUSHER');
    this.pusher.disconnect();
    this.pusher = null;
    this.pusherConfig = null;
    this.connectedTenantId = null;
  }

  public pusherIsConnected(): boolean {
    this.logger.info('pusherIsConnected()');
    if (!this.pusher) return false;
    const state = this.pusher.connection.state;
    console.log('## PUSHER state ##', state);
    return state === 'connected' || state === 'connecting';
  }
}